import { Tabs, TabsList, TabsTrigger, TabsContent } from "@repo/ui";
import {
  Building2,
  CreditCard,
  KeyRound,
  Settings,
  ShieldCheck,
  UserCircle,
} from "lucide-react";
import { GeneralOptionsSection } from "./general-options-section";
import { PharmacySecuritySection } from "./pharmacy-security-section";
import { PharmacyInfoForm } from "./pharmacy-info-form";
import { LegalComplianceForm } from "./legal-compliance-form";
import { PermissionsSection } from "./permissions-section";
import { ProfileSection } from "./profile-section";
import { SubscriptionSection } from "./subscription-section";
import { usePermissionStore } from "@/lib/stores/permission-store";

interface PharmacyData {
  id: string;
  legal_name: string;
  dba_name: string | null;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  phone_number: string;
  npi: string;
  ncpdp_id: string | null;
  dea_registration_number: string | null;
  dea_expiration_date: string | null;
  state_license_number: string;
  license_expiration_date: string;
  timezone: string;
  pharmacy_type: string;
  subscription_status: string;
  subscription_started_at: string | null;
  subscription_ends_at: string | null;
  trial_ends_at: string | null;
  stripe_customer_id: string | null;
}

interface UserData {
  id: string;
  name: string;
  role: string;
  initials: string;
}

interface SettingsTabsProps {
  pharmacy: PharmacyData;
  user: UserData;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onPharmacyUpdate: () => void;
}

export function SettingsTabs({
  pharmacy,
  user,
  activeTab,
  onTabChange,
  onPharmacyUpdate,
}: SettingsTabsProps) {
  const check = usePermissionStore((s) => s.check);
  // Subscribe to overrides so permission checks re-evaluate when overrides change
  const _overrides = usePermissionStore((s) => s.overrides);
  const generalResult = check("settings:general");
  const settingsResult = check("settings:pharmacy");
  const subscriptionResult = check("settings:subscription");
  const permissionsResult = check("users:deactivate");
  const canViewGeneral =
    generalResult !== null && generalResult.type !== "denied";
  const canEditSettings =
    settingsResult !== null && settingsResult.type !== "denied";
  const canManageSubscription =
    subscriptionResult !== null && subscriptionResult.type !== "denied";
  const canManagePermissions =
    permissionsResult !== null && permissionsResult.type !== "denied";

  const resolvedTab = activeTab ?? (canViewGeneral ? "general" : "pharmacy");

  return (
    <Tabs
      value={resolvedTab}
      onValueChange={onTabChange}
      className="w-full"
      data-testid="settings-tabs"
    >
      <TabsList className="w-full justify-start gap-1 bg-muted/50 p-1 h-auto flex-wrap">
        {canViewGeneral && (
          <TabsTrigger
            value="general"
            className="gap-2 data-[state=active]:bg-background"
            data-testid="settings-tab-general"
          >
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">General</span>
            <span className="sm:hidden">General</span>
          </TabsTrigger>
        )}
        <TabsTrigger
          value="pharmacy"
          className="gap-2 data-[state=active]:bg-background"
          data-testid="settings-tab-pharmacy"
        >
          <Building2 className="h-4 w-4" />
          <span className="hidden sm:inline">Pharmacy Info</span>
          <span className="sm:hidden">Pharmacy</span>
        </TabsTrigger>
        <TabsTrigger
          value="legal"
          className="gap-2 data-[state=active]:bg-background"
          data-testid="settings-tab-legal"
        >
          <ShieldCheck className="h-4 w-4" />
          <span className="hidden sm:inline">Legal & Compliance</span>
          <span className="sm:hidden">Legal</span>
        </TabsTrigger>
        <TabsTrigger
          value="profile"
          className="gap-2 data-[state=active]:bg-background"
          data-testid="settings-tab-profile"
        >
          <UserCircle className="h-4 w-4" />
          <span className="hidden sm:inline">My Profile</span>
          <span className="sm:hidden">Profile</span>
        </TabsTrigger>
        {canManagePermissions && (
          <TabsTrigger
            value="permissions"
            className="gap-2 data-[state=active]:bg-background"
            data-testid="settings-tab-permissions"
          >
            <KeyRound className="h-4 w-4" />
            <span className="hidden sm:inline">Permissions</span>
            <span className="sm:hidden">Perms</span>
          </TabsTrigger>
        )}
        {canManageSubscription && (
          <TabsTrigger
            value="subscription"
            className="gap-2 data-[state=active]:bg-background"
            data-testid="settings-tab-subscription"
          >
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Subscription & Billing</span>
            <span className="sm:hidden">Billing</span>
          </TabsTrigger>
        )}
      </TabsList>

      {canViewGeneral && (
        <TabsContent
          value="general"
          className="mt-6 space-y-6"
          data-testid="settings-panel-general"
        >
          <GeneralOptionsSection />
          <PharmacySecuritySection />
        </TabsContent>
      )}

      <TabsContent
        value="pharmacy"
        className="mt-6"
        data-testid="settings-panel-pharmacy"
      >
        <PharmacyInfoForm
          pharmacy={pharmacy}
          canEditSettings={canEditSettings}
          onSuccess={onPharmacyUpdate}
        />
      </TabsContent>

      <TabsContent
        value="legal"
        className="mt-6"
        data-testid="settings-panel-legal"
      >
        <LegalComplianceForm
          pharmacy={pharmacy}
          canEditSettings={canEditSettings}
          onSuccess={onPharmacyUpdate}
        />
      </TabsContent>

      <TabsContent
        value="profile"
        className="mt-6"
        data-testid="settings-panel-profile"
      >
        <ProfileSection user={user} canEditSettings={canEditSettings} />
      </TabsContent>

      {canManagePermissions && (
        <TabsContent
          value="permissions"
          className="mt-6"
          data-testid="settings-panel-permissions"
        >
          <PermissionsSection />
        </TabsContent>
      )}

      {canManageSubscription && (
        <TabsContent
          value="subscription"
          className="mt-6"
          data-testid="settings-panel-subscription"
        >
          <SubscriptionSection pharmacy={pharmacy} />
        </TabsContent>
      )}
    </Tabs>
  );
}
